import React, { useState } from 'react';

interface CFDTradingFormsProps {
  currentPrice: number;
  usdtBalance: number;
  selectedPair: string;
  onCFDTrade?: (
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number }
  ) => void;
  onPropChallengeTrade?: (
    challengeId: string,
    symbol: string,
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit'
  ) => void;
  challengeId?: string;
  isPropChallenge?: boolean;
}

const CFDTradingForms: React.FC<CFDTradingFormsProps> = ({
  currentPrice,
  usdtBalance,
  selectedPair,
  onCFDTrade,
  onPropChallengeTrade,
  challengeId,
  isPropChallenge = false
}) => {
  const [marginType, setMarginType] = useState<'isolated' | 'cross'>('isolated');
  const [leverage, setLeverage] = useState(10);
  const [orderType, setOrderType] = useState<'limit' | 'market'>('market');
  const [longAmount, setLongAmount] = useState('');
  const [shortAmount, setShortAmount] = useState('');
  const [longPercentage, setLongPercentage] = useState(0);
  const [shortPercentage, setShortPercentage] = useState(0);

  // Stop Loss / Take Profit states
  const [longStopLoss, setLongStopLoss] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [longTakeProfit, setLongTakeProfit] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [shortStopLoss, setShortStopLoss] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [shortTakeProfit, setShortTakeProfit] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });

  const leverageOptions = [1, 5, 10, 20, 30, 50];
  const percentageOptions = [25, 50, 75, 100];

  // Get instrument type for display
  const getInstrumentType = () => {
    if (selectedPair.includes('USD') && !selectedPair.includes('USDT')) {
      if (selectedPair.startsWith('XAU') || selectedPair.startsWith('XAG') || selectedPair.startsWith('WTI') || selectedPair.startsWith('XPT')) {
        return 'Commodity';
      }
      return 'Forex';
    }
    return 'Stock';
  };

  // Get contract size based on instrument type
  const getContractSize = () => {
    const type = getInstrumentType();
    switch (type) {
      case 'Forex': return 100000; // Standard lot
      case 'Commodity': 
        if (selectedPair.startsWith('XAU')) return 100; // 100 oz Gold
        if (selectedPair.startsWith('XAG')) return 5000; // 5000 oz Silver
        if (selectedPair.startsWith('WTI')) return 1000; // 1000 barrels Oil
        return 100;
      case 'Stock': return 1; // 1 share
      default: return 1;
    }
  };

  // Get minimum trade size
  const getMinTradeSize = () => {
    const type = getInstrumentType();
    switch (type) {
      case 'Forex': return 0.01; // 0.01 lots
      case 'Commodity': return 0.1;
      case 'Stock': return 1;
      default: return 0.01;
    }
  };

  // Get spread (simulated)
  const getSpread = () => {
    const type = getInstrumentType();
    switch (type) {
      case 'Forex': return currentPrice * 0.00002; // 2 pips
      case 'Commodity': return currentPrice * 0.0005; // 0.05%
      case 'Stock': return currentPrice * 0.001; // 0.1%
      default: return currentPrice * 0.0001;
    }
  };

  // Calculate estimated PnL for stop loss and take profit
  const calculateEstimatedPnl = (
    side: 'long' | 'short',
    entryPrice: number,
    triggerPrice: number,
    amount: number,
    leverage: number
  ): number => {
    const contractSize = getContractSize();
    const notionalValue = amount * entryPrice * contractSize;
    
    if (side === 'long') {
      // For long positions: (triggerPrice - entryPrice) * amount * contractSize * leverage
      return (triggerPrice - entryPrice) * amount * contractSize * leverage;
    } else {
      // For short positions: (entryPrice - triggerPrice) * amount * contractSize * leverage
      return (entryPrice - triggerPrice) * amount * contractSize * leverage;
    }
  };

  const handleLong = () => {
    const amount = parseFloat(longAmount);
    if (!amount || amount < getMinTradeSize()) {
      alert(`Minimum trade size is ${getMinTradeSize()}`);
      return;
    }

    const contractSize = getContractSize();
    const notionalValue = amount * currentPrice * contractSize;
    const requiredMargin = notionalValue / leverage;
    
    if (requiredMargin > usdtBalance) {
      alert('Insufficient margin balance');
      return;
    }

    if (isPropChallenge && onPropChallengeTrade && challengeId) {
      // For prop challenge, use the prop challenge handler
      console.log(`CFDTradingForms.handleLong: Using onPropChallengeTrade for challenge ${challengeId}`);
      
      const stopLoss = longStopLoss.enabled && longStopLoss.price ? {
        trigger_price: parseFloat(longStopLoss.price),
        execution_type: longStopLoss.type,
        execution_price: longStopLoss.type === 'limit' && longStopLoss.limitPrice ? parseFloat(longStopLoss.limitPrice) : undefined,
      } : undefined;

      const takeProfit = longTakeProfit.enabled && longTakeProfit.price ? {
        trigger_price: parseFloat(longTakeProfit.price),
        execution_type: longTakeProfit.type,
        execution_price: longTakeProfit.type === 'limit' && longTakeProfit.limitPrice ? parseFloat(longTakeProfit.limitPrice) : undefined,
      } : undefined;

      onPropChallengeTrade(challengeId, selectedPair, 'long', amount, leverage, marginType, stopLoss, takeProfit, 'market');
      
      // Reset form
      setLongAmount('');
      setLongPercentage(0);
      setLongStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
      setLongTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });
    } else if (onCFDTrade) {
      // For regular CFD trading, use the CFD trading handler
      console.log(`CFDTradingForms.handleLong: Using onCFDTrade`);
      
      const stopLoss = longStopLoss.enabled && longStopLoss.price ? {
        trigger_price: parseFloat(longStopLoss.price),
        execution_type: longStopLoss.type,
        execution_price: longStopLoss.type === 'limit' && longStopLoss.limitPrice ? parseFloat(longStopLoss.limitPrice) : undefined,
      } : undefined;

      const takeProfit = longTakeProfit.enabled && longTakeProfit.price ? {
        trigger_price: parseFloat(longTakeProfit.price),
        execution_type: longTakeProfit.type,
        execution_price: longTakeProfit.type === 'limit' && longTakeProfit.limitPrice ? parseFloat(longTakeProfit.limitPrice) : undefined,
      } : undefined;

      onCFDTrade('long', amount, leverage, marginType, stopLoss, takeProfit);
      
      // Reset form
      setLongAmount('');
      setLongPercentage(0);
      setLongStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
      setLongTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });
    }
  };

  const handleShort = () => {
    const amount = parseFloat(shortAmount);
    if (!amount || amount < getMinTradeSize()) {
      alert(`Minimum trade size is ${getMinTradeSize()}`);
      return;
    }

    const contractSize = getContractSize();
    const notionalValue = amount * currentPrice * contractSize;
    const requiredMargin = notionalValue / leverage;
    
    if (requiredMargin > usdtBalance) {
      alert('Insufficient margin balance');
      return;
    }

    if (isPropChallenge && onPropChallengeTrade && challengeId) {
      // For prop challenge, use the prop challenge handler
      console.log(`CFDTradingForms.handleShort: Using onPropChallengeTrade for challenge ${challengeId}`);
      
      const stopLoss = shortStopLoss.enabled && shortStopLoss.price ? {
        trigger_price: parseFloat(shortStopLoss.price),
        execution_type: shortStopLoss.type,
        execution_price: shortStopLoss.type === 'limit' && shortStopLoss.limitPrice ? parseFloat(shortStopLoss.limitPrice) : undefined,
      } : undefined;

      const takeProfit = shortTakeProfit.enabled && shortTakeProfit.price ? {
        trigger_price: parseFloat(shortTakeProfit.price),
        execution_type: shortTakeProfit.type,
        execution_price: shortTakeProfit.type === 'limit' && shortTakeProfit.limitPrice ? parseFloat(shortTakeProfit.limitPrice) : undefined,
      } : undefined;

      onPropChallengeTrade(challengeId, selectedPair, 'short', amount, leverage, marginType, stopLoss, takeProfit, 'market');
      
      // Reset form
      setShortAmount('');
      setShortPercentage(0);
      setShortStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
      setShortTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });
    } else if (onCFDTrade) {
      // For regular CFD trading, use the CFD trading handler
      console.log(`CFDTradingForms.handleShort: Using onCFDTrade`);
      
      const stopLoss = shortStopLoss.enabled && shortStopLoss.price ? {
        trigger_price: parseFloat(shortStopLoss.price),
        execution_type: shortStopLoss.type,
        execution_price: shortStopLoss.type === 'limit' && shortStopLoss.limitPrice ? parseFloat(shortStopLoss.limitPrice) : undefined,
      } : undefined;

      const takeProfit = shortTakeProfit.enabled && shortTakeProfit.price ? {
        trigger_price: parseFloat(shortTakeProfit.price),
        execution_type: shortTakeProfit.type,
        execution_price: shortTakeProfit.type === 'limit' && shortTakeProfit.limitPrice ? parseFloat(shortTakeProfit.limitPrice) : undefined,
      } : undefined;

      onCFDTrade('short', amount, leverage, marginType, stopLoss, takeProfit);
      
      // Reset form
      setShortAmount('');
      setShortPercentage(0);
      setShortStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
      setShortTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });
    }
  };

  const handleLongPercentage = (percentage: number) => {
    setLongPercentage(percentage);
    const availableForBuy = usdtBalance;
    const maxNotional = availableForBuy * leverage;
    const contractSize = getContractSize();
    const maxAmount = maxNotional / (currentPrice * contractSize);
    const amount = (maxAmount * percentage) / 100;
    setLongAmount(amount.toFixed(getInstrumentType() === 'Stock' ? 0 : 2));
  };

  const handleShortPercentage = (percentage: number) => {
    setShortPercentage(percentage);
    const availableForBuy = usdtBalance;
    const maxNotional = availableForBuy * leverage;
    const contractSize = getContractSize();
    const maxAmount = maxNotional / (currentPrice * contractSize);
    const amount = (maxAmount * percentage) / 100;
    setShortAmount(amount.toFixed(getInstrumentType() === 'Stock' ? 0 : 2));
  };

  const calculateLongCost = () => {
    const amount = parseFloat(longAmount) || 0;
    const contractSize = getContractSize();
    const notionalValue = amount * currentPrice * contractSize;
    return notionalValue / leverage;
  };

  const calculateShortCost = () => {
    const amount = parseFloat(shortAmount) || 0;
    const contractSize = getContractSize();
    const notionalValue = amount * currentPrice * contractSize;
    return notionalValue / leverage;
  };

  // Calculate estimated PnL for long stop loss
  const longStopLossPnl = longStopLoss.enabled && longStopLoss.price && longAmount
    ? calculateEstimatedPnl(
        'long',
        currentPrice,
        parseFloat(longStopLoss.price),
        parseFloat(longAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for long take profit
  const longTakeProfitPnl = longTakeProfit.enabled && longTakeProfit.price && longAmount
    ? calculateEstimatedPnl(
        'long',
        currentPrice,
        parseFloat(longTakeProfit.price),
        parseFloat(longAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for short stop loss
  const shortStopLossPnl = shortStopLoss.enabled && shortStopLoss.price && shortAmount
    ? calculateEstimatedPnl(
        'short',
        currentPrice,
        parseFloat(shortStopLoss.price),
        parseFloat(shortAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for short take profit
  const shortTakeProfitPnl = shortTakeProfit.enabled && shortTakeProfit.price && shortAmount
    ? calculateEstimatedPnl(
        'short',
        currentPrice,
        parseFloat(shortTakeProfit.price),
        parseFloat(shortAmount),
        leverage
      )
    : null;

  const instrumentType = getInstrumentType();
  const contractSize = getContractSize();
  const spread = getSpread();
  const minTradeSize = getMinTradeSize();

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
      {/* Instrument Info */}
      <div className="mb-6 bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-400">Type:</span>
            <div className="font-medium text-white">{instrumentType}</div>
          </div>
          <div>
            <span className="text-slate-400">Contract Size:</span>
            <div className="font-medium text-white">{contractSize.toLocaleString()}</div>
          </div>
          <div>
            <span className="text-slate-400">Spread:</span>
            <div className="font-medium text-white">{spread.toFixed(instrumentType === 'Forex' ? 5 : 2)}</div>
          </div>
          <div>
            <span className="text-slate-400">Min Size:</span>
            <div className="font-medium text-white">{minTradeSize}</div>
          </div>
        </div>
      </div>

      {/* Margin Type and Leverage Controls */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-6">
          <div className="flex bg-slate-900/50 rounded-xl p-1 border border-slate-600/30">
            <button
              onClick={() => setMarginType('isolated')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                marginType === 'isolated'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              Isolated
            </button>
            <button
              onClick={() => setMarginType('cross')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                marginType === 'cross'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              Cross
            </button>
          </div>
          
          <div className="text-orange-400 font-bold text-2xl bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
            {leverage}x
          </div>
        </div>

        {/* Leverage Selector */}
        <div className="flex items-center gap-4">
          <span className="text-slate-400 text-sm">Leverage:</span>
          <div className="flex gap-2">
            {leverageOptions.map((lev) => (
              <button
                key={lev}
                onClick={() => setLeverage(lev)}
                className={`px-3 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                  leverage === lev
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 transform scale-105'
                    : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-600/30'
                }`}
              >
                {lev}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Order Type Tabs */}
      <div className="flex gap-6 mb-8">
        <button
          onClick={() => setOrderType('market')}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
            orderType === 'market' 
              ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          Market
        </button>
        <button
          onClick={() => setOrderType('limit')}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
            orderType === 'limit' 
              ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          Limit
        </button>
      </div>

      <div className="flex gap-8">
        {/* Long Form */}
        <div className="flex-1">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-400 mb-3">
              <span>Available Margin</span>
              <span className="flex items-center gap-2 text-emerald-400 font-mono">
                {usdtBalance.toFixed(2)} USD
                <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Price Input */}
            <div>
              <label className="block text-sm text-slate-400 mb-3">Price (USD)</label>
              <input
                type="text"
                value={currentPrice.toFixed(instrumentType === 'Forex' ? 5 : 2)}
                className="w-full bg-transparent text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm text-slate-400 mb-3">
                Size ({instrumentType === 'Forex' ? 'Lots' : instrumentType === 'Stock' ? 'Shares' : 'Units'})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={`Min ${minTradeSize}`}
                  value={longAmount}
                  onChange={(e) => {
                    setLongAmount(e.target.value);
                    setLongPercentage(0);
                  }}
                  className="w-full bg-transparent text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm font-medium">
                  {instrumentType === 'Forex' ? 'LOTS' : instrumentType === 'Stock' ? 'SHARES' : 'UNITS'}
                </span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-2">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleLongPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    longPercentage === percentage 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/25 transform scale-105' 
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-white border border-slate-600/30'
                  }`}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss */}
            <div className="border-t border-slate-700/50 pt-6">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={longStopLoss.enabled}
                  onChange={(e) => setLongStopLoss(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-red-500 focus:ring-red-500/50"
                />
                <label className="text-sm text-slate-400">Stop Loss</label>
              </div>
              {longStopLoss.enabled && (
                <div className="space-y-3 bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder="Stop price"
                      value={longStopLoss.price}
                      onChange={(e) => setLongStopLoss(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-red-500/30 focus:outline-none focus:ring-1 focus:ring-red-500/50 text-sm font-mono"
                    />
                    {longStopLossPnl !== null && (
                      <div className="ml-2 text-red-400 text-sm font-medium whitespace-nowrap">
                        ${longStopLossPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={longStopLoss.type}
                      onChange={(e) => setLongStopLoss(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-red-500/30 text-sm custom-select"
                    >
                      <option value="market">Market</option>
                      <option value="limit">Limit</option>
                    </select>
                    {longStopLoss.type === 'limit' && (
                      <input
                        type="text"
                        placeholder="Limit price"
                        value={longStopLoss.limitPrice}
                        onChange={(e) => setLongStopLoss(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-red-500/30 text-sm font-mono"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={longTakeProfit.enabled}
                  onChange={(e) => setLongTakeProfit(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-emerald-500 focus:ring-emerald-500/50"
                />
                <label className="text-sm text-slate-400">Take Profit</label>
              </div>
              {longTakeProfit.enabled && (
                <div className="space-y-3 bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder="Target price"
                      value={longTakeProfit.price}
                      onChange={(e) => setLongTakeProfit(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-emerald-500/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-mono"
                    />
                    {longTakeProfitPnl !== null && (
                      <div className="ml-2 text-emerald-400 text-sm font-medium whitespace-nowrap">
                        ${longTakeProfitPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={longTakeProfit.type}
                      onChange={(e) => setLongTakeProfit(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-emerald-500/30 text-sm custom-select"
                    >
                      <option value="market">Market</option>
                      <option value="limit">Limit</option>
                    </select>
                    {longTakeProfit.type === 'limit' && (
                      <input
                        type="text"
                        placeholder="Limit price"
                        value={longTakeProfit.limitPrice}
                        onChange={(e) => setLongTakeProfit(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-emerald-500/30 text-sm font-mono"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>Required Margin</span>
                <span>Spread: {spread.toFixed(instrumentType === 'Forex' ? 5 : 2)}</span>
              </div>
              <div className="text-slate-300 font-mono text-lg">{calculateLongCost().toFixed(2)} USD</div>
            </div>

            <button 
              onClick={handleLong}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/25 transform hover:scale-105"
            >
              BUY/LONG
            </button>
          </div>
        </div>

        {/* Short Form */}
        <div className="flex-1">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-400 mb-3">
              <span>Available Margin</span>
              <span className="flex items-center gap-2 text-emerald-400 font-mono">
                {usdtBalance.toFixed(2)} USD
                <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Price Input */}
            <div>
              <label className="block text-sm text-slate-400 mb-3">Price (USD)</label>
              <input
                type="text"
                value={currentPrice.toFixed(instrumentType === 'Forex' ? 5 : 2)}
                className="w-full bg-transparent text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm text-slate-400 mb-3">
                Size ({instrumentType === 'Forex' ? 'Lots' : instrumentType === 'Stock' ? 'Shares' : 'Units'})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={`Min ${minTradeSize}`}
                  value={shortAmount}
                  onChange={(e) => {
                    setShortAmount(e.target.value);
                    setShortPercentage(0);
                  }}
                  className="w-full bg-transparent text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm font-medium">
                  {instrumentType === 'Forex' ? 'LOTS' : instrumentType === 'Stock' ? 'SHARES' : 'UNITS'}
                </span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-2">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleShortPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    shortPercentage === percentage 
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg shadow-red-500/25 transform scale-105' 
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-white border border-slate-600/30'
                  }`}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss */}
            <div className="border-t border-slate-700/50 pt-6">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={shortStopLoss.enabled}
                  onChange={(e) => setShortStopLoss(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-red-500 focus:ring-red-500/50"
                />
                <label className="text-sm text-slate-400">Stop Loss</label>
              </div>
              {shortStopLoss.enabled && (
                <div className="space-y-3 bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder="Stop price"
                      value={shortStopLoss.price}
                      onChange={(e) => setShortStopLoss(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-red-500/30 focus:outline-none focus:ring-1 focus:ring-red-500/50 text-sm font-mono"
                    />
                    {shortStopLossPnl !== null && (
                      <div className="ml-2 text-red-400 text-sm font-medium whitespace-nowrap">
                        ${shortStopLossPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={shortStopLoss.type}
                      onChange={(e) => setShortStopLoss(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-red-500/30 text-sm custom-select"
                    >
                      <option value="market">Market</option>
                      <option value="limit">Limit</option>
                    </select>
                    {shortStopLoss.type === 'limit' && (
                      <input
                        type="text"
                        placeholder="Limit price"
                        value={shortStopLoss.limitPrice}
                        onChange={(e) => setShortStopLoss(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-red-500/30 text-sm font-mono"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={shortTakeProfit.enabled}
                  onChange={(e) => setShortTakeProfit(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-emerald-500 focus:ring-emerald-500/50"
                />
                <label className="text-sm text-slate-400">Take Profit</label>
              </div>
              {shortTakeProfit.enabled && (
                <div className="space-y-3 bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder="Target price"
                      value={shortTakeProfit.price}
                      onChange={(e) => setShortTakeProfit(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-emerald-500/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-mono"
                    />
                    {shortTakeProfitPnl !== null && (
                      <div className="ml-2 text-emerald-400 text-sm font-medium whitespace-nowrap">
                        ${shortTakeProfitPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={shortTakeProfit.type}
                      onChange={(e) => setShortTakeProfit(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-emerald-500/30 text-sm custom-select"
                    >
                      <option value="market">Market</option>
                      <option value="limit">Limit</option>
                    </select>
                    {shortTakeProfit.type === 'limit' && (
                      <input
                        type="text"
                        placeholder="Limit price"
                        value={shortTakeProfit.limitPrice}
                        onChange={(e) => setShortTakeProfit(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-emerald-500/30 text-sm font-mono"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>Required Margin</span>
                <span>Spread: {spread.toFixed(instrumentType === 'Forex' ? 5 : 2)}</span>
              </div>
              <div className="text-slate-300 font-mono text-lg">{calculateShortCost().toFixed(2)} USD</div>
            </div>

            <button 
              onClick={handleShort}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-red-500/25 transform hover:scale-105"
            >
              SELL/SHORT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CFDTradingForms;